import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useSlotRecipe} from '@chakra-ui/react3'
import {FormattedNumber, useIntl} from 'react-intl'
import {useCurrency} from '../../../../hooks'
import {hasValidPrice} from '../../../utils/utils'
import priceProductRecipe from './index.recipe'

/**
 * PriceProduct component displays a formatted price with optional strikethrough list price.
 * @component
 * @param {Object} props - Component properties.
 * @param {number} props.value - The current price value; returns null if not provided.
 * @param {number} [props.valueListPrice] - The original list price; shown as strikethrough when greater than value.
 * @param {string} [props.currency] - The currency code (e.g., 'USD', 'EUR'). Defaults to the user's locale currency.
 * @param {string} [props.label] - Optional label prefix displayed before the price (e.g., 'From').
 * @param {boolean} [props.hidePromotionSignals] - When true, suppresses the strike-through list price.
 * @param {'primary'|'secondary'|'on-media'|'compact'} [props.variant] - Visual variant.
 * @returns {JSX.Element|null} Rendered price display or null when value is absent.
 */
const PriceProduct = ({
    value,
    valueListPrice,
    currency,
    label,
    hidePromotionSignals,
    layout,
    variant
}) => {
    const {currency: activeCurrency} = useCurrency()
    const intl = useIntl()
    currency = currency || activeCurrency

    const recipe = useSlotRecipe({recipe: priceProductRecipe})
    const styles = useMemo(() => recipe({layout, variant}), [recipe, layout, variant])

    const shouldShowStrikethrough = useMemo(() => {
        return !hidePromotionSignals && hasValidPrice(valueListPrice) && valueListPrice > value
    }, [hidePromotionSignals, value, valueListPrice])

    if (value == null) {
        return null
    }

    const priceAriaLabel = shouldShowStrikethrough
        ? `${intl.formatMessage({
              id: 'item_attributes.label.discounted_price',
              defaultMessage: 'Discounted price:'
          })} ${label ? `${label} ` : ''}${intl.formatNumber(value, {
              style: 'currency',
              currency
          })}`
        : undefined

    const listPriceAriaLabel = shouldShowStrikethrough
        ? `${intl.formatMessage({
              id: 'item_attributes.label.original_price',
              defaultMessage: 'Original price:'
          })} ${intl.formatNumber(valueListPrice, {style: 'currency', currency})}`
        : undefined

    return (
        <Box css={styles.container}>
            <Text css={styles.price} aria-label={priceAriaLabel}>
                <span>{label ? `${label} ` : ''}</span>
                <FormattedNumber value={value} currency={currency} style="currency" />
            </Text>
            {shouldShowStrikethrough && (
                <Text css={styles.listPrice} aria-label={listPriceAriaLabel}>
                    <FormattedNumber value={valueListPrice} currency={currency} style="currency" />
                </Text>
            )}
        </Box>
    )
}

PriceProduct.propTypes = {
    value: PropTypes.number,
    valueListPrice: PropTypes.number,
    currency: PropTypes.string,
    label: PropTypes.string,
    hidePromotionSignals: PropTypes.bool,
    layout: PropTypes.oneOf(Object.keys(priceProductRecipe.variants.layout)),
    variant: PropTypes.oneOf(['primary', 'secondary', 'on-media', 'compact'])
}

export default PriceProduct
